import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { apiClient } from "@/lib/api";
import { identify } from "@/lib/analytics";

const AuthContext = createContext(null);

function _detectClientLocale() {
  if (typeof window === "undefined") return {};
  const out = {};
  try {
    out.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("[auth] timezone detection failed:", e?.message);
    out.timezone = null;
  }
  try {
    const lang = navigator.language || null;
    if (lang) {
      out.locale = lang;
      out.language_code = lang.split("-")[0].toLowerCase();
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("[auth] locale detection failed:", e?.message);
  }
  return out;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    try {
      const r = await apiClient.get("/auth/me");
      setUser(r.data);

      // PostHog identify with rich segmentation properties.
      try {
        if (r.data?.user_id) {
          identify(r.data.user_id, {
            email: r.data.email,
            tier: r.data.tier,
            country: r.data.country || null,
            currency: r.data.currency || "USD",
            language_code: r.data.language_code || "en",
            locale: r.data.locale || null,
            timezone: r.data.timezone || null,
            has_subscription: !!r.data.has_subscription,
            subscription_plan: r.data.subscription_plan || null,
          });
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("[auth] posthog identify failed:", e?.message);
      }

      // Best-effort: sync any locale fields the client knows that the server doesn't yet.
      try {
        const det = _detectClientLocale();
        const patch = {};
        if (det.timezone && det.timezone !== r.data?.timezone) patch.timezone = det.timezone;
        if (det.locale && det.locale !== r.data?.locale) patch.locale = det.locale;
        if (det.language_code && det.language_code !== r.data?.language_code) patch.language_code = det.language_code;
        if (Object.keys(patch).length > 0) {
          apiClient.post("/profile/locale", patch).catch((err) => {
            // eslint-disable-next-line no-console
            console.warn("[auth] locale patch failed:", err?.message);
          });
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("[auth] locale sync block failed:", e?.message);
      }

      return r.data;
    } catch (e) {
      if (e?.response?.status && e.response.status !== 401) {
        console.warn("auth/me failed:", e?.message);
      }
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    fetchMe();
  }, [fetchMe]);

  const logout = async () => {
    try {
      await apiClient.post("/auth/logout");
    } catch (e) {
      // Continue with client-side logout even if server call fails
      console.warn("Server logout failed (clearing client state anyway):", e?.message);
    }
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, refresh: fetchMe, fetchMe, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
